// pages/ogretmen/SinavRaporlari.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  useTheme,
  SelectChangeEvent,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  styled,
  useMediaQuery,
  Skeleton,
  Divider,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns/format';
import { tr } from 'date-fns/locale';
import AssessmentIcon from '@mui/icons-material/Assessment';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

import { SinavRaporRequestModal } from '../../../types/RequestModals/SinavRaporRequestModal';
import { getSinavSiralama, getSinifVeSinavListesi } from '../../../services/api/sinavRaporService';
import { SinavRaporBaseRequestModal } from '../../../types/RequestModals/SinavRaporBaseRequestModal';
import { SinavRaporError } from '../../../types/ErrorModals/SinavRaporError';
import { SinavDetayRequestModal } from '../../../types/RequestModals/SinavDetayRequestModal';
import { SinavDetayResponseModal } from '../../../types/modals';
import { SinavDetayModal } from '../../../layouts/SinavDetayModal';
import { getSinavDetay, getSinavSonucPdf } from '../../../services/api/sinavService';

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  transition: 'transform 0.3s, box-shadow 0.3s',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: theme.shadows[8],
  },
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
}));

const ActionButton = styled(IconButton)(({ theme }) => ({
  transition: 'transform 0.2s',
  '&:hover': {
    transform: 'scale(1.15)',
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: "bold", 
  backgroundColor: theme.palette.primary.main, 
  color: "white",
  py: 2
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": { backgroundColor: theme.palette.action.hover },
  "&:hover": {
    backgroundColor: "rgba(232, 137, 16, 0.1)",
    transition: "background-color 0.3s ease",
  },
}));

const SinavRaporlari: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const [selectedSeason, setSelectedSeason] = useState<string>('2025');
  const [selectedSinav, setSelectedSinav] = useState<number | ''>('');
  const [selectedSinif, setSelectedSinif] = useState<number | ''>('');
  const [openModal, setOpenModal] = useState(false);
  const [selectedSinavDetay, setSelectedSinavDetay] = useState<SinavDetayResponseModal | null>(null);
  
  const availableSeasons = ['2023', '2024', '2025', '2026', '2027'];
  
  const handleSeasonChange = (event: SelectChangeEvent) => {
    setSelectedSeason(event.target.value);
    setSelectedSinav('');
    setSelectedSinif('');
  };
  
  const handleSinavChange = (event: SelectChangeEvent<number | ''>) => {
    setSelectedSinav(event.target.value as number);
  };
  
  const handleSinifChange = (event: SelectChangeEvent<number | ''>) => {
    setSelectedSinif(event.target.value as number);
  };
  
  // Sınıf ve sınav listesini getir
  const {
    data: listData,
    isLoading: isListLoading,
    error: listError,
    refetch: refetchLists
  } = useQuery({
    queryKey: ['sinifVeSinavListesi', selectedSeason],
    queryFn: async () => {
      const request: SinavRaporBaseRequestModal = {
        kurumKodu: localStorage.getItem('kurumKodu') || '',
        sezon: parseInt(selectedSeason) + 1
      };
      return await getSinifVeSinavListesi(request);
    },
    enabled: true,
    retry: 1
  });
  
  // Sınav sıralama raporu
  const {
    data: siralamaData,
    isLoading: isSiralamaLoading,
    error: siralamaError,
    refetch: refetchSiralama
  } = useQuery({
    queryKey: ['sinavSiralama', selectedSeason, selectedSinav, selectedSinif],
    queryFn: async () => {
      const request: SinavRaporRequestModal = {
        kurumKodu: localStorage.getItem('kurumKodu') || '',
        sezon: parseInt(selectedSeason) + 1,
        sinavId: selectedSinav as number,
        sinifId: selectedSinif as number
      };
      return await getSinavSiralama(request);
    },
    enabled: !!selectedSinav && !!selectedSinif,
    retry: 1
  });
  
  // Sezon değiştiğinde listeleri yeniden getir
  useEffect(() => {
    if (selectedSeason) {
      refetchLists();
    }
  }, [selectedSeason, refetchLists]);
  
  // Sınav veya sınıf değiştiğinde raporları yeniden getir
  useEffect(() => {
    if (selectedSinav && selectedSinif) {
      refetchSiralama();
    }
  }, [selectedSinav, selectedSinif, refetchSiralama]);
  
  // Öğrenci detay bilgilerini görüntüle
  const handleViewStudentDetails = async (ogrenciId: number) => {
    try {
      if (!selectedSinav) return;
      
      const requestData: SinavDetayRequestModal = {
        kurumKodu: localStorage.getItem('kurumKodu') || '',
        sinavId: selectedSinav as number,
        ogrenciId: ogrenciId,
        sezon: parseInt(selectedSeason) + 1
      };
      
      const response = await getSinavDetay(requestData);
      setSelectedSinavDetay(response);
      setOpenModal(true);
    } catch (error) {
      console.error('Öğrenci sınav detayları görüntüleme hatası:', error);
    }
  };
  
  // Öğrenci sınav sonuç PDF'ini indir
  const handleDownloadStudentPdf = async (ogrenciId: number) => {
    try {
      if (!selectedSinav) return;
      
      const requestData: SinavDetayRequestModal = {
        kurumKodu: localStorage.getItem('kurumKodu') || '',
        sinavId: selectedSinav as number,
        ogrenciId: ogrenciId,
        sezon: parseInt(selectedSeason) + 1
      };
      
      const response = await getSinavSonucPdf(requestData);
      const blob = new Blob([response], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `sinav_sonuc_${selectedSinav}_${ogrenciId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('PDF indirme hatası:', error);
    }
  };
  
  // Yükleme iskeletleri
  const renderSkeletons = () => {
    if (isTablet) {
      return (
        <Grid container spacing={2}>
          {[1, 2, 3, 4].map((item) => (
            <Grid item xs={12} sm={6} md={4} key={item}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Skeleton variant="rectangular" width={80} height={24} sx={{ mb: 2 }} />
                  <Skeleton variant="text" height={40} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width="60%" height={20} sx={{ mb: 2 }} />
                  <Skeleton variant="rectangular" height={1} sx={{ my: 1.5 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-around', mt: 2 }}>
                    <Skeleton variant="circular" width={40} height={40} />
                    <Skeleton variant="circular" width={40} height={40} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      );
    }
    
    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {[1, 2, 3, 4, 5].map((item) => (
                <TableCell key={item} sx={{ backgroundColor: theme.palette.primary.main }}>
                  <Skeleton variant="text" sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {[1, 2, 3, 4, 5].map((row) => (
              <TableRow key={row}>
                {[1, 2, 3, 4, 5].map((cell) => (
                  <TableCell key={cell}>
                    <Skeleton variant="text" />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };
  
  // Sınav sıralama tablosu
  const renderSiralamaTable = () => {
    if (!siralamaData || !siralamaData.ogrenciListesi.length) {
      return (
        <Alert severity="info">
          Seçilen sınav ve sınıf için sıralama bilgisi bulunamadı.
        </Alert>
      );
    }
    
    if (isTablet) {
      // Mobil ve tablet için kart görünümü
      return (
        <Grid container spacing={2}>
          {siralamaData.ogrenciListesi.map((ogrenci, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <StyledCard elevation={3}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" component="h2" gutterBottom noWrap>
                      {ogrenci.ogrenciAdi}
                    </Typography>
                    <Chip 
                      label={`#${ogrenci.puanListesi[0]?.sinifSira || 'N/A'}`}
                      size="small"
                      color="primary"
                      sx={{ fontWeight: 'bold' }}
                    />
                  </Box>
                  
                  <Divider sx={{ my: 1.5 }} />
                  
                  <Box sx={{ my: 2 }}>
                    {ogrenci.puanListesi.map((puan, i) => (
                      <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          {puan.puanAdi}:
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {puan.puan.toFixed(2)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                  
                  <Divider sx={{ my: 1.5 }} />
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-around', mt: 2 }}>
                    <Tooltip title="Detayları Görüntüle">
                      <ActionButton
                        onClick={() => handleViewStudentDetails(parseInt(ogrenci.ogrenciNo))}
                        color="info"
                        size="medium"
                      >
                        <VisibilityIcon />
                      </ActionButton>
                    </Tooltip>
                    
                    <Tooltip title="PDF olarak İndir">
                      <ActionButton
                        onClick={() => handleDownloadStudentPdf(parseInt(ogrenci.ogrenciNo))}
                        color="error"
                        size="medium"
                      >
                        <PictureAsPdfIcon />
                      </ActionButton>
                    </Tooltip>
                  </Box>
                </CardContent>
              </StyledCard>
            </Grid>
          ))}
        </Grid>
      );
    }
    
    // Masaüstü için tablo görünümü
    return (
      <TableContainer 
        component={Paper} 
        elevation={3} 
        sx={{ 
          borderRadius: theme.shape.borderRadius * 1.5,
          overflow: 'hidden'
        }}
      >
        <Table aria-label="sınav sıralama tablosu">
          <TableHead>
            <TableRow>
              <StyledTableCell>Öğrenci</StyledTableCell>
              {siralamaData.ogrenciListesi[0]?.puanListesi.map((puan, index) => (
                <StyledTableCell key={index} align="center">{puan.puanAdi}</StyledTableCell>
              ))}
              <StyledTableCell align="center">İşlemler</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {siralamaData.ogrenciListesi.map((ogrenci, index) => (
              <StyledTableRow key={index}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2" fontWeight="medium">
                      {ogrenci.ogrenciAdi}
                    </Typography>
                    <Chip 
                      label={`#${ogrenci.puanListesi[0]?.sinifSira || 'N/A'}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ ml: 1, fontWeight: 'bold' }}
                    />
                  </Box>
                </TableCell>
                
                {ogrenci.puanListesi.map((puan, pIndex) => (
                  <TableCell key={pIndex} align="center">
                    <Tooltip title={`Genel Sıra: ${puan.genelSira}, Sınıf Sıra: ${puan.sinifSira}`} arrow>
                      <Chip 
                        label={puan.puan.toFixed(2)} 
                        size="small" 
                        color="primary" 
                        variant={pIndex === 0 ? "filled" : "outlined"}
                      />
                    </Tooltip>
                  </TableCell>
                ))}
                
                <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                    <Tooltip title="Detayları Görüntüle" arrow>
                      <ActionButton
                        onClick={() => handleViewStudentDetails(parseInt(ogrenci.ogrenciNo))}
                        sx={{
                          color: theme.palette.info.main,
                        }}
                        size="small"
                      >
                        <VisibilityIcon />
                      </ActionButton>
                    </Tooltip>
                    <Tooltip title="PDF olarak İndir" arrow>
                      <ActionButton
                        onClick={() => handleDownloadStudentPdf(parseInt(ogrenci.ogrenciNo))}
                        sx={{
                          color: theme.palette.error.main,
                        }}
                        size="small"
                      >
                        <PictureAsPdfIcon />
                      </ActionButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };
  
  return (
    <>
      {selectedSinavDetay && (
        <SinavDetayModal
          open={openModal}
          onClose={() => setOpenModal(false)}
          sinavDetay={selectedSinavDetay}
        />
      )}
      
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography 
          variant="h5" 
          component="h1" 
          gutterBottom
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            fontWeight: 600,
            color: theme.palette.primary.main
          }}
        >
          <AssessmentIcon sx={{ mr: 1 }} /> 
          Sınav Raporları
        </Typography>
        
        <Paper 
          elevation={3} 
          sx={{ 
            p: 3, 
            mb: 4, 
            borderRadius: theme.shape.borderRadius * 1.5 
          }}
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel id="season-select-label">Eğitim Sezonu</InputLabel>
                <Select
                  labelId="season-select-label"
                  id="season-select"
                  value={selectedSeason}
                  onChange={handleSeasonChange}
                  label="Eğitim Sezonu"
                >
                  {availableSeasons.map((season) => (
                    <MenuItem key={season} value={season}>
                      {season} - {(parseInt(season) + 1)} Eğitim Yılı
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth variant="outlined" size="small" disabled={isListLoading || !!listError}>
                <InputLabel id="sinav-select-label">Sınav</InputLabel>
                <Select
                  labelId="sinav-select-label"
                  id="sinav-select"
                  value={selectedSinav}
                  onChange={handleSinavChange}
                  label="Sınav"
                >
                  {listData?.sinavListesi.map((sinav) => (
                    <MenuItem key={sinav.sinavId} value={sinav.sinavId}>
                      {sinav.sinavAdi} ({format(new Date(sinav.sinavTarihi), 'dd MMM yyyy', { locale: tr })})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth variant="outlined" size="small" disabled={isListLoading || !!listError}>
                <InputLabel id="sinif-select-label">Sınıf</InputLabel>
                <Select
                  labelId="sinif-select-label"
                  id="sinif-select"
                  value={selectedSinif}
                  onChange={handleSinifChange}
                  label="Sınıf"
                >
                  {listData?.sinifListesi.map((sinif) => (
                    <MenuItem key={sinif.sinifId} value={sinif.sinifId}>
                      {sinif.sinifAdi}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          
          {isListLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <CircularProgress />
            </Box>
          )}
          
          {listError && (
            <Alert 
              severity="error" 
              sx={{ mt: 3 }}
            >
              {(listError as SinavRaporError).message || "Sınıf ve sınav listesi yüklenirken bir hata oluştu."}
            </Alert>
          )}
        </Paper>
        
        {selectedSinav && selectedSinif && (
          <Paper 
            elevation={3} 
            sx={{ 
              borderRadius: theme.shape.borderRadius * 1.5,
              overflow: 'hidden',
              p: 3
            }}
          >
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Öğrenci Sıralaması
              </Typography>
              {siralamaData && (
                <Typography variant="body2" color="text.secondary">
                  {siralamaData.sinifAdi} sınıfının {siralamaData.sinavAdi} sınavı sonuçları aşağıda listelenmiştir.
                </Typography>
              )}
            </Box>
            
            {isSiralamaLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : siralamaError ? (
              <Alert severity="error">
                {(siralamaError as SinavRaporError).message || "Sınav sıralama raporu yüklenirken bir hata oluştu."}
              </Alert>
            ) : siralamaData ? (
              renderSiralamaTable()
            ) : (
              <Alert severity="info">
                Lütfen rapor için bir sınav ve sınıf seçin.
              </Alert>
            )}
          </Paper>
        )}
      </Box>
    </>
  );
};

export default SinavRaporlari;